/**
 * CfoPortalPage — the Hospital CFO portal (HON-006, REV-047, AI-005).
 *
 * Wireframe 5 (CFO side). A distinct, executive-styled, client-facing surface a
 * hospital CFO sees: a branded header band, headline financial KPIs (revenue
 * captured, AR outstanding, conversion), KPI cards with trend deltas, an inline
 * revenue trend chart (CSS bars — no chart lib), program-health indicators, a
 * mock Power BI embed placeholder, and "Ask PFA / Performance Insights" — a
 * natural-language query box whose advisory answer renders via an AIInsightCard
 * with supporting figures + an account drill-down (AI-005, advisory/HITL).
 *
 * Data via useCfoDashboard() / useAskPfa(); both degrade gracefully to a seed on
 * 404 so the executive view always renders the full design.
 */
import { useState, type CSSProperties } from 'react';
import { useAuth } from '../auth/useAuth';
import { hospitalLabel } from '../components/nav';
import {
  useCfoDashboard,
  useAskPfa,
  type CfoKpi,
  type ProgramHealthIndicator,
} from '../hooks/useCfoPortal';
import { colors, radius, shadow, space, tints } from '../theme';
import { Card } from '../components/Card';
import { AIInsightCard } from '../components/AIInsightCard';
import { RoleImpactStrip } from '../components/PlatformImpactStrip';
import { RevenueProjectionCard } from '../components/RevenueProjectionCard';
import { StatusBadge } from '../components/StatusBadge';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { useShellSlot } from '../components/shellContext';

function fmtCurrency(n: number): string {
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function fmtDelta(delta: number | null | undefined): { text: string; color: string } | null {
  if (delta === null || delta === undefined) return null;
  const up = delta >= 0;
  return { text: `${up ? '▲' : '▼'} ${Math.abs(Math.round(delta * 100))}%`, color: up ? colors.success : colors.danger };
}

const SUGGESTED_QUESTIONS = [
  'Which accounts are driving our 60+ day AR?',
  'How is enrollment conversion trending this quarter?',
  'What is our projected revenue capture for next month?',
];

function healthTier(status: ProgramHealthIndicator['status']): 'success' | 'warning' | 'danger' {
  if (status === 'healthy') return 'success';
  if (status === 'watch') return 'warning';
  return 'danger';
}

function healthLabel(status: ProgramHealthIndicator['status']): string {
  if (status === 'healthy') return 'Healthy';
  if (status === 'watch') return 'Watch';
  return 'At risk';
}

export function CfoPortalPage() {
  useShellSlot({ breadcrumb: 'CFO Portal' });
  const { tenantId } = useAuth();
  const { dashboard, available, isLoading, error, refresh } = useCfoDashboard();
  const askPfa = useAskPfa();
  const [question, setQuestion] = useState('');

  const handleAsk = (e: React.FormEvent) => {
    e.preventDefault();
    void askPfa.ask(question);
  };

  if (isLoading && !dashboard) {
    return (
      <Card>
        <LoadingIndicator text="Loading CFO dashboard…" />
      </Card>
    );
  }
  if (!dashboard) return null;

  const trendMax = Math.max(...dashboard.trend.map((t) => t.value), 1);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: space.lg }}>
      {/* Branded, client-facing header band */}
      <header style={heroBand}>
        <div style={heroAccent} aria-hidden />
        <div style={heroInner}>
          <div>
            <div style={heroEyebrow}>Platform for Financial Advocacy · Executive Portal</div>
            <h1 style={heroTitle}>{hospitalLabel(tenantId)}</h1>
            <div style={heroSub}>
              Revenue &amp; coverage performance · updated {new Date(dashboard.generated_at).toLocaleString()}
            </div>
          </div>
          <div style={heroHeadline}>
            <div style={heroHeadlineLabel}>Revenue captured (MTD)</div>
            <div style={heroHeadlineValue}>{fmtCurrency(dashboard.revenue_captured)}</div>
            <div style={heroHeadlineMeta}>
              {fmtCurrency(dashboard.ar_outstanding)} AR outstanding · {Math.round(dashboard.conversion_rate * 100)}% conversion
            </div>
          </div>
        </div>
      </header>

      {!available && (
        <p style={seedNotice} role="status">
          Showing a representative dashboard. Live tenant figures appear automatically once the CFO
          portal endpoint is online.
        </p>
      )}
      {error && (
        <p style={errorText} role="alert">
          {error.message}
        </p>
      )}

      <RoleImpactStrip />

      {/* KPI cards */}
      <div style={kpiStrip}>
        {dashboard.kpis.map((k: CfoKpi) => {
          const delta = fmtDelta(k.delta);
          return (
            <div key={k.key} style={kpiCard}>
              <div style={kpiValue}>{k.display}</div>
              <div style={kpiLabel}>{k.label}</div>
              {delta && <div style={{ ...kpiDelta, color: delta.color }}>{delta.text} vs prior</div>}
            </div>
          );
        })}
      </div>

      <div style={twoCol}>
        {/* Revenue trend chart (inline CSS bars) */}
        <Card
          title="Revenue captured — trend"
          actions={
            <button type="button" className="btn btn-secondary btn-sm" onClick={refresh} disabled={isLoading}>
              Refresh
            </button>
          }
        >
          <div style={chartWrap}>
            {dashboard.trend.map((t) => (
              <div key={t.label} style={chartCol}>
                <div style={chartBarTrack}>
                  <div style={{ ...chartBar, height: `${(t.value / trendMax) * 100}%` }} title={fmtCurrency(t.value)} />
                </div>
                <div style={chartColLabel}>{t.label}</div>
                <div style={chartColValue}>{`$${Math.round(t.value / 1000)}K`}</div>
              </div>
            ))}
          </div>
        </Card>

        {/* Program-health indicators */}
        <Card title="Program health">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {dashboard.program_health.map((p) => (
              <div key={p.label} style={healthRow}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={healthName}>{p.label}</span>
                  <StatusBadge status={healthTier(p.status)} label={healthLabel(p.status)} />
                </div>
                <div style={healthDetail}>{p.detail}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Forward-looking revenue projection (AI-004) — a forward view, not a
          backward-looking report. Previously only on the admin Intelligence page. */}
      <RevenueProjectionCard />

      <div style={twoCol}>
        {/* AR aging breakdown */}
        <Card title="Accounts-receivable aging">
          <div style={{ marginBottom: 12 }}>
            <div style={arTotal}>{fmtCurrency(dashboard.ar_outstanding)}</div>
            <div style={kpiLabel}>total outstanding</div>
          </div>
          <table style={arTable}>
            <thead>
              <tr>
                <th style={arTh}>Bucket</th>
                <th style={{ ...arTh, textAlign: 'right' }}>Balance</th>
                <th style={{ ...arTh, textAlign: 'right' }}>Invoices</th>
              </tr>
            </thead>
            <tbody>
              {dashboard.ar_buckets.map((b) => (
                <tr key={b.label}>
                  <td style={arTd}>{b.label}</td>
                  <td style={{ ...arTd, textAlign: 'right' }}>{fmtCurrency(b.balance)}</td>
                  <td style={{ ...arTd, textAlign: 'right' }}>{b.invoice_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        {/* Mock Power BI embed */}
        <Card title="Embedded report (Power BI)">
          <div style={pbiPlaceholder}>
            <div style={pbiIcon} aria-hidden>
              📊
            </div>
            <div style={{ fontWeight: 600 }}>{dashboard.embedded_report.report_name}</div>
            <div style={muted}>
              In production this renders a tenant-scoped Power BI report embedded via an RLS-bound embed
              token. Row-level security keys on tenant_id so this hospital only sees its own data
              (FND-003, REV-047).
            </div>
            <div style={pbiMeta}>
              RLS tenant: <code>{dashboard.embedded_report.rls_tenant_id}</code>
              {dashboard.embedded_report.is_mock && <span style={mockTag}>mock embed</span>}
            </div>
          </div>
        </Card>
      </div>

      {/* Ask PFA / Performance Insights (AI-005) */}
      <Card title="Ask PFA · Performance Insights">
        <form onSubmit={handleAsk} style={askForm}>
          <input
            type="text"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            className="input"
            placeholder="Ask about revenue, AR, conversion, or projections…"
            aria-label="Ask PFA a performance question"
            style={{ flex: 1 }}
          />
          <button type="submit" className="btn btn-primary" disabled={askPfa.isAsking || !question.trim()}>
            {askPfa.isAsking ? 'Thinking…' : 'Ask'}
          </button>
        </form>
        <div style={suggestRow}>
          {SUGGESTED_QUESTIONS.map((q) => (
            <button
              key={q}
              type="button"
              style={suggestChip}
              onClick={() => {
                setQuestion(q);
                void askPfa.ask(q);
              }}
            >
              {q}
            </button>
          ))}
        </div>

        {askPfa.error && (
          <p style={errorText} role="alert">
            {askPfa.error.message}
          </p>
        )}

        {askPfa.answer && (
          <div style={{ marginTop: 16 }}>
            <AIInsightCard
              confidence={askPfa.answer.confidence}
              heading={askPfa.answer.question}
              acceptLabel="Use this answer"
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>{askPfa.answer.answer}</div>

                {askPfa.answer.figures.length > 0 && (
                  <div style={figureRow}>
                    {askPfa.answer.figures.map((f) => (
                      <div key={f.label} style={figureCard}>
                        <div style={figureValue}>{f.value}</div>
                        <div style={figureLabel}>{f.label}</div>
                      </div>
                    ))}
                  </div>
                )}

                {askPfa.answer.accounts.length > 0 && (
                  <div>
                    <div style={drillTitle}>Account drill-down</div>
                    <table style={arTable}>
                      <thead>
                        <tr>
                          <th style={arTh}>Account</th>
                          <th style={{ ...arTh, textAlign: 'right' }}>Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {askPfa.answer.accounts.map((a) => (
                          <tr key={a.account}>
                            <td style={{ ...arTd, fontWeight: 600, color: colors.action }}>{a.account}</td>
                            <td style={{ ...arTd, textAlign: 'right' }}>{a.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div style={{ fontSize: '0.74rem', color: colors.textMuted }}>{askPfa.answer.disclosure}</div>

                {!askPfa.live && (
                  <div style={muted}>
                    Representative answer — connects to the live Performance Insights model when its
                    endpoint is online.
                  </div>
                )}
              </div>
            </AIInsightCard>
          </div>
        )}
      </Card>
    </div>
  );
}

// --- styles ---------------------------------------------------------------

const heroBand: CSSProperties = {
  position: 'relative',
  display: 'flex',
  overflow: 'hidden',
  borderRadius: radius.lg,
  background: `linear-gradient(135deg, ${colors.brand} 0%, ${colors.brandActive} 100%)`,
  boxShadow: shadow.md,
};
const heroAccent: CSSProperties = { width: 6, background: colors.action, flexShrink: 0 };
const heroInner: CSSProperties = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 24,
  padding: '24px 28px',
  flexWrap: 'wrap',
};
const heroEyebrow: CSSProperties = {
  fontSize: '0.72rem',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: '#C4B5FD',
};
const heroTitle: CSSProperties = { margin: '6px 0 2px', fontSize: '1.7rem', fontWeight: 700, color: '#FFFFFF' };
const heroSub: CSSProperties = { fontSize: '0.86rem', color: '#DDD6FE' };
const heroHeadline: CSSProperties = {
  textAlign: 'right',
  background: 'rgba(255,255,255,0.08)',
  borderRadius: radius.md,
  padding: '14px 20px',
  border: '1px solid rgba(255,255,255,0.14)',
};
const heroHeadlineLabel: CSSProperties = {
  fontSize: '0.72rem',
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  color: '#C4B5FD',
};
const heroHeadlineValue: CSSProperties = { fontSize: '2rem', fontWeight: 800, color: '#FFFFFF', lineHeight: 1.1, marginTop: 2 };
const heroHeadlineMeta: CSSProperties = { fontSize: '0.8rem', color: '#DDD6FE', marginTop: 4 };

const seedNotice: CSSProperties = {
  margin: 0,
  padding: '8px 12px',
  background: colors.brandTint,
  color: colors.brandActive,
  borderRadius: radius.sm,
  fontSize: '0.82rem',
};
const errorText: CSSProperties = { color: colors.danger, fontSize: '0.9rem', margin: 0 };

const kpiStrip: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: 12,
};
const kpiCard: CSSProperties = {
  background: colors.white,
  border: `1px solid ${colors.border}`,
  borderRadius: radius.md,
  padding: '16px 18px',
  boxShadow: shadow.sm,
};
const kpiValue: CSSProperties = { fontSize: '1.6rem', fontWeight: 800, lineHeight: 1.1, color: colors.textPrimary };
const kpiLabel: CSSProperties = { fontSize: '0.78rem', color: colors.textSecondary, marginTop: 4 };
const kpiDelta: CSSProperties = { fontSize: '0.78rem', fontWeight: 600, marginTop: 6 };

const twoCol: CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: space.lg };

const chartWrap: CSSProperties = { display: 'flex', alignItems: 'flex-end', gap: 14, height: 200, paddingTop: 8 };
const chartCol: CSSProperties = { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' };
const chartBarTrack: CSSProperties = { flex: 1, width: '100%', display: 'flex', alignItems: 'flex-end', minHeight: 0 };
const chartBar: CSSProperties = {
  width: '100%',
  maxWidth: 44,
  margin: '0 auto',
  background: `linear-gradient(180deg, ${colors.actionHover}, ${colors.action})`,
  borderRadius: `${radius.sm} ${radius.sm} 0 0`,
};
const chartColLabel: CSSProperties = { fontSize: '0.76rem', color: colors.textSecondary, marginTop: 6 };
const chartColValue: CSSProperties = { fontSize: '0.74rem', fontWeight: 700, color: colors.textPrimary };

const healthRow: CSSProperties = {
  padding: '10px 12px',
  border: `1px solid ${colors.border}`,
  borderRadius: radius.md,
  background: colors.surface,
};
const healthName: CSSProperties = { fontSize: '0.9rem', fontWeight: 600, color: colors.textPrimary };
const healthDetail: CSSProperties = { fontSize: '0.82rem', color: colors.textSecondary, marginTop: 4, lineHeight: 1.45 };

const arTotal: CSSProperties = { fontSize: '1.5rem', fontWeight: 800, color: colors.textPrimary };
const arTable: CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' };
const arTh: CSSProperties = {
  textAlign: 'left',
  padding: '6px 8px',
  borderBottom: `2px solid ${colors.border}`,
  color: colors.textSecondary,
  fontWeight: 600,
  fontSize: '0.74rem',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
};
const arTd: CSSProperties = { padding: '7px 8px', borderBottom: `1px solid ${colors.border}`, color: colors.textPrimary };

const pbiPlaceholder: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  gap: 8,
  padding: '28px 24px',
  background: colors.surface,
  border: `1px dashed ${colors.borderStrong}`,
  borderRadius: radius.md,
};
const pbiIcon: CSSProperties = { fontSize: '2rem' };
const muted: CSSProperties = { color: colors.textSecondary, fontSize: '0.82rem', lineHeight: 1.5, maxWidth: 460 };
const pbiMeta: CSSProperties = { fontSize: '0.78rem', color: colors.textSecondary, display: 'flex', alignItems: 'center', gap: 8 };
const mockTag: CSSProperties = {
  fontSize: '0.68rem',
  fontWeight: 700,
  padding: '1px 8px',
  borderRadius: radius.pill,
  background: tints.neutral,
  color: tints.neutralText,
};

const askForm: CSSProperties = { display: 'flex', gap: 8, flexWrap: 'wrap' };
const suggestRow: CSSProperties = { display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 };
const suggestChip: CSSProperties = {
  padding: '5px 12px',
  border: `1px solid ${colors.border}`,
  borderRadius: radius.pill,
  background: colors.white,
  color: colors.textSecondary,
  fontSize: '0.8rem',
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'inherit',
};
const figureRow: CSSProperties = { display: 'flex', gap: 10, flexWrap: 'wrap' };
const figureCard: CSSProperties = {
  flex: '1 1 120px',
  background: colors.white,
  border: `1px solid ${colors.border}`,
  borderRadius: radius.md,
  padding: '10px 12px',
};
const figureValue: CSSProperties = { fontSize: '1.1rem', fontWeight: 800, color: colors.textPrimary };
const figureLabel: CSSProperties = { fontSize: '0.74rem', color: colors.textSecondary, marginTop: 2 };
const drillTitle: CSSProperties = {
  fontSize: '0.72rem',
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  color: colors.textSecondary,
  marginBottom: 6,
};
